import React, { useState, useEffect } from 'react';
import { Modal, message } from 'antd';
import Select from '../../../Components/Select';
import TextareaComponent from '../../../Components/Textarea';
import { getMedications, linkMedicationToTreatment } from '../../../services/kollectaHealthCore';
import styles from './style.module.css';
import LeftIcon from './leftIcon.svg';
import { usePatient } from '../../../Contexts/PatientContext';

const NovoTratamentoModal = ({ visible, onClose, onTreatmentAdded }) => {
  const { patientData, updatePatient } = usePatient();
  const [medicationData, setMedicationData] = useState({
    medication: '',
    observations: '',
    dosage: '-'
  });

  const [medicationsList, setMedicationsList] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingTreatment, setSavingTreatment] = useState(false);

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        setLoading(true);
        const category = patientData?.type === 'efeitos-adversos' ? 'effects' : undefined;
        const response = await getMedications(null, category);
        setMedications(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar medicamentos:', error);
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      fetchMedications();
    }
  }, [visible, patientData?.type]);

  const handleMedicationDataChange = (field, value) => {
    console.log('handleMedicationDataChange:', field, value);
    const actualValue = value?.target?.value || value;
    setMedicationData(prevState => ({
      ...prevState,
      [field]: actualValue
    }));
  };

  const handleAddMedication = () => {
    console.log('handleAddMedication - medicationData:', medicationData);
    if (medicationData.medication) {
      const selectedMedication = medications.find(med => med.id === medicationData.medication);
      console.log('Medicamento selecionado:', selectedMedication);
      
      const newMedication = { 
        ...medicationData,
        medicationName: selectedMedication?.name || medicationData.medication
      };
      console.log('Novo medicamento a ser adicionado:', newMedication);
      
      setMedicationsList(prevList => {
        const newList = [...prevList, newMedication];
        console.log('Nova lista de medicamentos:', newList);
        return newList;
      });
      
      setMedicationData(prev => ({
        ...prev,
        medication: '',
        observations: ''
      }));
    }
  };

  const handleSave = async () => {
    console.log('handleSave - medicationsList antes de enviar:', medicationsList);
    
    if (medicationData.medication) {
      console.log('Adicionando medicamento pendente antes de salvar:', medicationData);
      
      const selectedMedication = medications.find(med => med.id === medicationData.medication);
      const newMedication = { 
        ...medicationData,
        medicationName: selectedMedication?.name || medicationData.medication
      };
      
      const updatedList = [...medicationsList, newMedication];
      console.log('Lista atualizada com o último medicamento:', updatedList);
      
      await handleSaveTratamento(updatedList);
    } else {
      if (medicationsList.length === 0) {
        message.warning('Nenhum medicamento foi adicionado ao tratamento.');
        return;
      }
      
      console.log('Salvando lista de medicamentos:', medicationsList);
      await handleSaveTratamento(medicationsList);
    }
  };

  const handleSaveTratamento = async (medicationsList) => {
    try {
      setSavingTreatment(true);
      
      if (!medicationsList || medicationsList.length === 0) {
        message.warning('Nenhum medicamento foi adicionado ao tratamento.');
        onClose();
        return;
      }

      console.log('Iniciando vinculação de medicamentos:', medicationsList);

      const lastTreatment = patientData.treatments[patientData.treatments.length - 1];

      for (const med of medicationsList) {
        console.log('Processando medicamento para vinculação:', med);
        
        const medicationData = {
          medication: {
            connect: {
              id: med.medication
            }
          },
          observations: med.observations || "",
          frequency: "-",
          dosage: med.dosage || "-"
        };
        
        console.log('Dados para vinculação:', medicationData);
        
        try {
          const linkResponse = await linkMedicationToTreatment(lastTreatment.id, medicationData);
          console.log('Resposta da vinculação:', linkResponse);
          
          if (!linkResponse.data) {
            throw new Error('Resposta inválida da API');
          }
        } catch (linkError) {
          console.error('Erro ao vincular medicamento:', linkError);
          message.error(`Erro ao vincular medicamento ${med.medicationName}. Por favor, tente novamente.`);
          return;
        }
      }

      message.success('Medicamentos vinculados com sucesso!');
      
      setMedicationsList([]);
      setMedicationData({
        medication: '',
        observations: '',
        dosage: '-'
      });

      if (onTreatmentAdded) {
        console.log('NovoTratamentoModal - Chamando onTreatmentAdded callback');
        onTreatmentAdded();
      } else {
        onClose();
      }

    } catch (error) {
      console.error('Erro ao vincular medicamentos:', error);
      message.error('Erro ao vincular medicamentos. Por favor, tente novamente.');
    } finally {
      setSavingTreatment(false);
    }
  };

  const hasMedicationsToSave = medicationsList.length > 0 || medicationData.medication;

  return (
    <Modal
      title={
        <div className={styles.modalTitle}>
          <img src={LeftIcon} alt="Kollecta Health" />
          <h3>Adicionar medicamentos ao tratamento</h3>
        </div>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Salvar"
      cancelText="Cancelar"
      width="90%"
      style={{ maxWidth: '800px' }}
      className={styles.novoTratamentoModal}
      okButtonProps={{ 
        disabled: !hasMedicationsToSave || savingTreatment
      }}
      confirmLoading={savingTreatment}
      centered
    >
      <div className={styles.modalContent}>
        {medicationsList && medicationsList.length > 0 && (
          <div className={styles.medicationsList}>
            <h3>Medicamentos Adicionados:</h3>
            <ul>
              {medicationsList.map((med, index) => (
                <li key={index}>
                  {med.medicationName}
                  {med.observations && ` - Obs: ${med.observations}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.formSection}>
          <div className={styles.medicationRow}>
            <div className={styles.formGroup}>
              <label htmlFor="medication">Medicamento</label>
              <Select
                options={medications.map(med => ({ value: med.id, label: med.name }))}
                value={medicationData.medication}
                onChange={(value) => handleMedicationDataChange('medication', value)}
                label=""
                fullWidth
                disabled={loading}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="observations">Observações</label>
            <TextareaComponent
              onValueChange={(value) => handleMedicationDataChange('observations', value)}
              value={medicationData.observations}
            />
          </div>

          <div className={styles.formGroup}>          
            <button 
              type="button"
              className={`${styles.btnPrimary} ${styles.btnGray}`}
              onClick={handleAddMedication}
              disabled={!medicationData.medication || loading}
            >
              + Adicionar mais medicamentos à lista
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NovoTratamentoModal;
